export interface ResolvePricingCanceledStateInput {
    setupCardLoaded: boolean;
    setupCardSubscriptionStatus?: string | null;
    fallbackIsCanceled: boolean;
}

export interface PricingSummaryCardState {
    planCode?: string;
    billingCycle?: 'monthly' | 'yearly';
    buttonText?: string;
    disabled?: boolean;
    action?: string;
}

export interface ResolveSummaryCardStateInput {
    summaryCards: PricingSummaryCardState[];
    planCode: string;
    cycle: 'monthly' | 'yearly';
    effectiveCurrentPlanCode?: string;
    effectiveCurrentBillingCycle?: 'monthly' | 'yearly';
}

export const resolveExpiredPlanCardButtonText = (_planCode: string): string => 'Upgrade';

const normalizeStatus = (status?: string | null): string | null => {
    if (typeof status !== 'string') return null;
    const normalized = status.trim().toLowerCase();
    return normalized || null;
};

export const resolvePricingCanceledState = ({
    setupCardLoaded,
    setupCardSubscriptionStatus,
    fallbackIsCanceled,
}: ResolvePricingCanceledStateInput): boolean => {
    if (!setupCardLoaded) {
        return fallbackIsCanceled;
    }

    const normalizedStatus = normalizeStatus(setupCardSubscriptionStatus);
    if (!normalizedStatus) {
        return fallbackIsCanceled;
    }

    if (normalizedStatus === 'active' || normalizedStatus === 'trial' || normalizedStatus === 'trialing') {
        return false;
    }

    if (normalizedStatus === 'pending_cancellation' || normalizedStatus === 'canceled') {
        return true;
    }

    return fallbackIsCanceled;
};

const normalizePlanCode = (planCode?: string | null): string => (planCode || '').trim().toUpperCase();

const normalizeBillingCycle = (cycle?: string | null): 'monthly' | 'yearly' | null => {
    if (cycle === 'monthly' || cycle === 'yearly') return cycle;
    return null;
};

const isCurrentPlanOnlyAction = (card: PricingSummaryCardState): boolean => {
    const action = (card.action || '').trim().toLowerCase();
    const buttonText = (card.buttonText || '').trim().toLowerCase();
    return action === 'renew'
        || action === 'current_plan'
        || buttonText === 'renew'
        || buttonText === 'current plan';
};

export const resolveSummaryCardState = ({
    summaryCards,
    planCode,
    cycle,
    effectiveCurrentPlanCode,
    effectiveCurrentBillingCycle,
}: ResolveSummaryCardStateInput): PricingSummaryCardState | undefined => {
    const normalizedPlanCode = normalizePlanCode(planCode);
    const isExactCurrentCard = normalizePlanCode(effectiveCurrentPlanCode) === normalizedPlanCode
        && effectiveCurrentBillingCycle === cycle;
    const exactMatch = summaryCards.find((card) => (
        normalizePlanCode(card.planCode) === normalizedPlanCode
        && normalizeBillingCycle(card.billingCycle) === cycle
    ));

    if (exactMatch) {
        if (isCurrentPlanOnlyAction(exactMatch) && !isExactCurrentCard) {
            return undefined;
        }
        return exactMatch;
    }

    const fallbackMatch = summaryCards.find((card) => (
        normalizePlanCode(card.planCode) === normalizedPlanCode
        && !normalizeBillingCycle(card.billingCycle)
    ));

    if (!fallbackMatch) {
        return undefined;
    }

    if (isCurrentPlanOnlyAction(fallbackMatch)) {
        return isExactCurrentCard ? fallbackMatch : undefined;
    }

    return fallbackMatch;
};
